import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { getApiUrl } from "../../lib/api";

const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 750;

const PhotoAlbumPickerButton = ({ onSelection, disabled }) => {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const pickerWindowRef = useRef(null);
  const forceCloseIntervalRef = useRef(null);

  const handlePick = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/picker/session"), {
        method: "POST",
        credentials: "include",
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        
        // Handle need for re-consent
        if (res.status === 401 && errorData.needsReconsent) {
          const shouldReconsent = confirm("Google Photos access has expired. Would you like to refresh your permissions? This will redirect you to Google.");
          if (shouldReconsent) {
            window.location.href = `${process.env.REACT_APP_API_BASE_URL}/auth/google`;
          }
          throw new Error("Google Photos access expired.");
        }
        
        throw new Error(`Failed to create Picker session: ${errorData.error || res.status}`);
      }

      const { pickerUri, sessionId } = await res.json();
      const win = window.open(
        pickerUri,
        "GooglePhotosPicker",
        "width=600,height=800"
      );
      if (!win) throw new Error("Popup blocked. Please allow popups.");

      setSessionId(sessionId);
      pickerWindowRef.current = win;
    } catch (err) {
      setLoading(false);
      alert("Failed to open Google Photos Picker. Check console.");
      console.error("Picker error:", err);
    }
  };

  const handleStartOver = () => {
    console.log("🔁 Start Over clicked");
    try {
      pickerWindowRef.current?.close();
    } catch (_) {}

    setLoading(false);
    setSessionId(null);
    clearInterval(forceCloseIntervalRef.current);
  };

  useEffect(() => {
    if (!loading || !sessionId || !pickerWindowRef.current) return;

    let isCancelled = false;
    const startTime = Date.now();

    const poll = async () => {
      if (isCancelled) return;

      try {
        const statusRes = await fetch(
          getApiUrl(
            `/api/picker/session-status?sessionId=${encodeURIComponent(
              sessionId
            )}`
          ),
          { credentials: "include" }
        );
        
        if (!statusRes.ok) {
          const errorData = await statusRes.json().catch(() => ({}));
          
          if (statusRes.status === 401 && errorData.needsReconsent) {
            console.warn("Google Photos access expired during polling");
            setLoading(false);
            try { pickerWindowRef.current?.close(); } catch (_) {}
            
            const shouldReconsent = confirm("Google Photos access expired during photo selection. Would you like to refresh your permissions?");
            if (shouldReconsent) {
              window.location.href = `${process.env.REACT_APP_API_BASE_URL}/auth/google`;
            }
            return;
          }
          throw new Error(`Session status error: ${errorData.error || statusRes.status}`);
        }
        
        const statusData = await statusRes.json();

        if (statusData.mediaItemsSet) {
          setLoading(false);

          try {
            pickerWindowRef.current?.close();
          } catch (e) {
            console.warn("Error closing picker window:", e);
          }

          if (onSelection) onSelection({ sessionId, ...statusData });
        } else if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
          console.warn("Picker timeout reached.");
          setLoading(false);
          try {
            pickerWindowRef.current?.close();
          } catch (e) {
            console.warn("Error closing picker window after timeout:", e);
          }
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (err) {
        console.error("Polling error:", err);
        setLoading(false);
        try {
          pickerWindowRef.current?.close();
        } catch (_) {}
      }
    };

    const initialTimeout = setTimeout(poll, POLL_INTERVAL_MS);

    forceCloseIntervalRef.current = setInterval(() => {
      try {
        if (pickerWindowRef.current?.closed) {
          clearInterval(forceCloseIntervalRef.current);
        }
      } catch (_) {}
    }, 1000);

    return () => {
      isCancelled = true;
      clearTimeout(initialTimeout);
      clearInterval(forceCloseIntervalRef.current);
    };
  }, [loading, sessionId, onSelection]);

  return (
    <div className="flex flex-row items-center gap-3">
      <button
        onClick={handlePick}
        disabled={loading || disabled}
        className={`px-6 py-3 rounded-lg font-bold shadow-md transition ${
          loading || disabled
            ? "bg-gray-400 text-white cursor-not-allowed"
            : "bg-blue-700 hover:bg-blue-800 text-white"
        }`}
        type="button"
      >
        {loading
          ? "Waiting for album selection..."
          : "Pick Google Photos Album"}
      </button>

      {loading && (
        <button
          onClick={handleStartOver}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 transition"
        >
          Start Over
        </button>
      )}
    </div>
  );
};

PhotoAlbumPickerButton.propTypes = {
  onSelection: PropTypes.func,
  disabled: PropTypes.bool,
};

export default PhotoAlbumPickerButton;
